// Tenantra Inc. - Saved Cards Management Component
import { useState } from 'react';
import { CreditCard, Trash2, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/services/api';
import { toast } from 'sonner';

// Card brand logos/colors
const CARD_BRANDS = {
  VISA: { color: 'text-blue-600', bg: 'bg-blue-50' },
  MASTERCARD: { color: 'text-orange-600', bg: 'bg-orange-50' },
  AMEX: { color: 'text-blue-800', bg: 'bg-blue-100' },
  DISCOVER: { color: 'text-orange-500', bg: 'bg-orange-50' },
  DEFAULT: { color: 'text-slate-600', bg: 'bg-slate-50' }
};

export default function SavedCardsSection({ paymentMethods, onRefresh, onAddCard }) {
  const [deletingId, setDeletingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  const handleSetDefault = async (pmId) => {
    setSettingDefaultId(pmId);
    try {
      await api.post(`/billing/payment-methods/${pmId}/set-default`);
      toast.success('Default payment method updated');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set default');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleDelete = async (pmId) => {
    if (!window.confirm('Are you sure you want to remove this card?')) return;
    
    setDeletingId(pmId);
    try {
      await api.delete(`/billing/payment-methods/${pmId}`);
      toast.success('Card removed successfully');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove card');
    } finally {
      setDeletingId(null);
    }
  };

  const getBrandStyle = (brand) => CARD_BRANDS[brand] || CARD_BRANDS.DEFAULT;

  if (!paymentMethods || paymentMethods.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-800">Saved Cards</h2>
        </div>
        <div className="p-6 text-center">
          <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 mb-4">No saved payment methods</p>
          <Button
            onClick={onAddCard}
            size="sm"
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-first-card-btn"
          >
            Add Your First Card
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Saved Cards</h2>
        <Button
          onClick={onAddCard}
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          data-testid="add-another-card-btn"
        >
          + Add Card
        </Button>
      </div>
      
      <div className="divide-y divide-slate-100">
        {paymentMethods.map((card) => {
          const brandStyle = getBrandStyle(card.brand);
          const isDeleting = deletingId === card.id;
          const isSettingDefault = settingDefaultId === card.id;
          
          return (
            <div 
              key={card.id} 
              className="px-4 py-3 flex items-center justify-between hover:bg-slate-50"
              data-testid={`saved-card-${card.last4}`}
            >
              <div className="flex items-center gap-2.5">
                {/* Card Icon */}
                <div className={`w-9 h-6 rounded flex items-center justify-center ${brandStyle.bg} flex-shrink-0`}>
                  <CreditCard className={`w-4 h-4 ${brandStyle.color}`} />
                </div>
                
                {/* Card Details */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-slate-800">
                      {card.brand} •••• {card.last4}
                    </span>
                    {card.is_default && (
                      <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-medium">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    Expires {card.exp_month.toString().padStart(2, '0')}/{card.exp_year}
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                {!card.is_default && (
                  <Button
                    onClick={() => handleSetDefault(card.id)}
                    disabled={isSettingDefault || isDeleting}
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-slate-600 hover:text-teal-600"
                    data-testid={`set-default-${card.last4}`}
                  >
                    {isSettingDefault ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Star className="w-3 h-3 mr-1" />
                        Set Default
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  onClick={() => handleDelete(card.id)}
                  disabled={isDeleting || isSettingDefault}
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-slate-400 hover:text-red-600"
                  data-testid={`delete-card-${card.last4}`}
                >
                  {isDeleting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
